<template>
  <div class="sa">

    <!-- ════════════════════ DASHBOARD HEADER ════════════════════ -->
    <div class="sa-dash-header">
      <div class="sa-dash-header-top">
        <div>
          <div class="sa-eyebrow">MARKETING · AFFILIATE RECONCILIATION</div>
          <h1 class="sa-title">Awin / Shareasale</h1>
        </div>
        <div class="sa-header-actions">
          <div class="sa-stat-pill" v-if="orders.length > 0">
            <i class="fa-solid fa-ban"></i>
            <strong>{{ orders.length }}</strong>
            <span>cancelar</span>
          </div>
          <div class="sa-stat-pill" v-if="ordersPendientes.length > 0">
            <i class="fa-regular fa-clock"></i>
            <strong>{{ ordersPendientes.length }}</strong>
            <span>pendientes</span>
          </div>
          <div class="sa-stat-pill sa-stat-pill--total" v-if="orders.length > 0 || ordersPendientes.length > 0">
            <i class="fa-solid fa-dollar-sign"></i>
            <strong>{{ (totalSpend + totalSpendPendientes).toFixed(2) }}</strong>
            <span>USD afectados</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ════════════════════ UPLOAD CARD ════════════════════ -->
    <section class="sa-card">
      <header class="sa-card-head">
        <div class="sa-card-title">
          <span class="sa-chevron">›</span>
          <span>Importar CSV de Awin</span>
        </div>
      </header>
      <div class="sa-card-body">
        <p class="sa-card-desc">
          Exporta el CSV de Awin y súbelo aquí. Pulsa <strong>procesar</strong> para cruzar las transacciones contra los pedidos de Shopify y detectar los que hay que cancelar o están pendientes de envío.
        </p>
        <div class="sa-upload-row">
          <div class="sa-upload-input">
            <b-form-file
              v-model="csvInput"
              placeholder="Selecciona un archivo o arrástralo aquí..."
              drop-placeholder="Suelta el archivo..."
              accept=".csv"
            />
          </div>
          <button class="sa-btn sa-btn--primary" @click="procesar">
            <i class="fa-solid fa-bolt"></i>
            <span>Procesar</span>
          </button>
        </div>
      </div>
    </section>

    <!-- ════════════════════ LOADING ════════════════════ -->
    <div v-if="procesando" class="sa-loading">
      <b-spinner variant="primary" label="Procesando..."></b-spinner>
      <span>Buscando pedidos en Shopify...</span>
    </div>

    <!-- ════════════════════ EMPTY STATE ════════════════════ -->
    <div v-if="busquedaRealizada && orders.length === 0 && ordersPendientes.length === 0" class="sa-empty">
      <div class="sa-empty-icon">
        <i class="fa-solid fa-circle-check"></i>
      </div>
      <h3>Todo en orden</h3>
      <p>Búsqueda completada. No hay pedidos que cancelar ni pendientes de envío.</p>
    </div>

    <!-- ════════════════════ PEDIDOS CANCELADOS ════════════════════ -->
    <section class="sa-card" v-if="orders.length > 0">
      <header class="sa-card-head">
        <div class="sa-card-title">
          <span class="sa-chevron sa-chevron--danger">›</span>
          <span>Pedidos a cancelar</span>
        </div>
        <div class="sa-card-meta">
          <div class="sa-stat-pill sa-stat-pill--danger">
            <i class="fa-solid fa-ban"></i>
            <strong>{{ orders.length }}</strong>
            <span>órdenes</span>
          </div>
          <div class="sa-stat-pill sa-stat-pill--danger">
            <i class="fa-solid fa-dollar-sign"></i>
            <strong>{{ totalSpend.toFixed(2) }}</strong>
            <span>USD a cancelar</span>
          </div>
        </div>
      </header>
      <div class="sa-card-body">
        <div class="sa-grid">
          <article
            v-for="item in orders"
            :key="'cancel-'+item.id"
            class="sa-order sa-order--danger"
          >
            <div class="sa-order-head">
              <div class="sa-order-status">
                <span class="sa-order-dot"></span>
                CANCELADO
              </div>
              <div class="sa-order-name">{{ item.name }}</div>
            </div>

            <div class="sa-order-meta">
              <div class="sa-meta-row">
                <span class="sa-meta-k">Razón</span>
                <span class="sa-meta-v">{{ item.cancel_reason || '—' }}</span>
              </div>
              <div class="sa-meta-row">
                <span class="sa-meta-k">Order ID</span>
                <span class="sa-meta-v sa-mono">{{ item.id }}</span>
              </div>
              <div class="sa-meta-row">
                <span class="sa-meta-k">Trans. ID</span>
                <span class="sa-meta-v sa-mono">{{ item.shareasale ? item.shareasale.id : '—' }}</span>
              </div>
              <div class="sa-meta-row">
                <span class="sa-meta-k">Fecha</span>
                <span class="sa-meta-v sa-mono">{{ item.created_at ? item.created_at.slice(0, 16).replace('T', ' ') : '—' }}</span>
              </div>
            </div>

            <div class="sa-order-amounts">
              <div class="sa-amount">
                <div class="sa-amount-label">Comisión</div>
                <div class="sa-amount-value">${{ item.shareasale ? item.shareasale.commission : '0' }}</div>
              </div>
              <div class="sa-amount sa-amount--big">
                <div class="sa-amount-label">Total venta</div>
                <div class="sa-amount-value sa-amount-value--danger">
                  ${{ item.shareasale ? parseFloat(item.shareasale.sale_amount).toFixed(2) : '0.00' }}
                </div>
              </div>
            </div>

            <button class="sa-btn sa-btn--ghost-danger" @click="verEnShopify(item.id)">
              <i class="fa-brands fa-shopify"></i>
              <span>Ver en Shopify</span>
            </button>
          </article>
        </div>
      </div>
    </section>

    <!-- ════════════════════ PEDIDOS PENDIENTES ════════════════════ -->
    <section class="sa-card" v-if="ordersPendientes.length > 0">
      <header class="sa-card-head">
        <div class="sa-card-title">
          <span class="sa-chevron sa-chevron--warning">›</span>
          <span>Pendientes de envío</span>
        </div>
        <div class="sa-card-meta">
          <div class="sa-stat-pill sa-stat-pill--warning">
            <i class="fa-regular fa-clock"></i>
            <strong>{{ ordersPendientes.length }}</strong>
            <span>órdenes</span>
          </div>
          <div class="sa-stat-pill sa-stat-pill--warning">
            <i class="fa-solid fa-dollar-sign"></i>
            <strong>{{ totalSpendPendientes.toFixed(2) }}</strong>
            <span>USD pendientes</span>
          </div>
        </div>
      </header>
      <div class="sa-card-body">
        <div class="sa-grid">
          <article
            v-for="item in ordersPendientes"
            :key="'pending-'+item.id"
            class="sa-order sa-order--warning"
          >
            <div class="sa-order-head">
              <div class="sa-order-status sa-order-status--warning">
                <span class="sa-order-dot"></span>
                PENDIENTE DE ENVÍO
              </div>
              <div class="sa-order-name">{{ item.name }}</div>
            </div>

            <div class="sa-order-meta">
              <div class="sa-meta-row">
                <span class="sa-meta-k">Order ID</span>
                <span class="sa-meta-v sa-mono">{{ item.id }}</span>
              </div>
              <div class="sa-meta-row">
                <span class="sa-meta-k">Trans. ID</span>
                <span class="sa-meta-v sa-mono">{{ item.shareasale ? item.shareasale.id : '—' }}</span>
              </div>
              <div class="sa-meta-row">
                <span class="sa-meta-k">Fecha</span>
                <span class="sa-meta-v sa-mono">{{ item.created_at ? item.created_at.slice(0, 16).replace('T', ' ') : '—' }}</span>
              </div>
            </div>

            <div class="sa-order-amounts">
              <div class="sa-amount">
                <div class="sa-amount-label">Comisión</div>
                <div class="sa-amount-value">${{ item.shareasale ? item.shareasale.commission : '0' }}</div>
              </div>
              <div class="sa-amount sa-amount--big">
                <div class="sa-amount-label">Total venta</div>
                <div class="sa-amount-value sa-amount-value--warning">
                  ${{ item.shareasale ? parseFloat(item.shareasale.sale_amount).toFixed(2) : '0.00' }}
                </div>
              </div>
            </div>

            <button class="sa-btn sa-btn--ghost-warning" @click="verEnShopify(item.id)">
              <i class="fa-brands fa-shopify"></i>
              <span>Ver en Shopify</span>
            </button>
          </article>
        </div>
      </div>
    </section>

  </div>
</template>
  
  
  <script>
  import axios from '@axios'
  import vSelect from 'vue-select'
  import BCardCode from '@core/components/b-card-code'
  import { BRow, BCol, BFormFile, BButton, BSpinner } from 'bootstrap-vue'
  import { BTable } from 'bootstrap-vue'
  
  
  export default {
  
    components: {
          BCardCode,
          BRow,
          BCol,
          BFormFile,
          BButton,
          BSpinner,
          vSelect,
          BTable
      },
  
    data(){
      return{

        orders: [],
        ordersPendientes: [],
        totalSpend: 0,
        totalSpendPendientes: 0,
        csvInput: null,
        procesando: false,
        busquedaRealizada: false,
        
      }
    },
    methods:{
      verEnShopify(id){
        window.open('https://admin.shopify.com/store/struendo/orders/'+id, '_blank');

      },
      calculateTotalSpend() {
          this.totalSpend = this.orders.reduce((total, item) => {
              const commission = item.shareasale ? parseFloat(item.shareasale.commission) || 0 : 0;
              return total + commission;
          }, 0);
      },
      calculateTotalSpendPendientes() {
          this.totalSpendPendientes = this.ordersPendientes.reduce((total, item) => {
              const commission = item.shareasale ? parseFloat(item.shareasale.commission) || 0 : 0;
              return total + commission;
          }, 0);
      },
      procesar(){
      this.orders = [];
      this.ordersPendientes = [];
      this.procesando = true;
      this.busquedaRealizada = false;

      var reader = new FileReader();
      var self = this;
      reader.onload = (e) => {
        let data = reader.result.split(/\r?\n/).filter(l => l.trim() !== "");

        // La primera línea son las cabeceras
        let headers = data[0].split(",").map(h => h.replace(/^"|"$/g, ""));

        let dataExport = data.slice(1).map(line => {
          let values = line.split(",").map(v => v.replace(/^"|"$/g, ""));
          let obj = {};
          headers.forEach((h, i) => {
            obj[h] = values[i] ?? null;
          });
          return obj;
        });


        let shopifyIds = [];

        dataExport.forEach(element => {
          if(element.extra != "")
            shopifyIds.push(element.extra);
        });

        
        
        axios
          .post("/shopify/ordersById", { shopifyIds })
          .then((response) => {
            return response.data;
          })
          .then((res) => {

                        
            res.forEach(element => {
              // Buscar por name (ambos con #), order_number, o ID interno
              const shareasaleData = dataExport.find((item) =>
                item.extra === element.name ||
                item.extra === '#' + element.order_number ||
                item.extra === element.order_number.toString() ||
                item.extra === element.id.toString()
              );
              element.shareasale = shareasaleData || null;

              // Pedidos cancelados
              if(element.cancel_reason != null) {
                this.orders.push(element);
              }
              // Pedidos pendientes de envío (no cancelados y sin fulfillment)
              else if(element.fulfillment_status === null) {
                this.ordersPendientes.push(element);
              }
            });

            this.calculateTotalSpendPendientes();
            this.procesando = false;
            this.busquedaRealizada = true;

          });

        //console.log(dataExport);


      }

        reader.readAsText(this.csvInput); 
        
        /*
        axios.post("/uso", {seccion:"shareASale"});

        navigator.clipboard.readText().then((text) => {
            const lines = text.trim().split("\n");
            const headers = lines[0].split("\t");
            const data = lines.slice(1).map(line => {
              const values = line.split("\t");
              return Object.fromEntries(headers.map((header, i) => [header, values[i] || ""]));
            });

            let shopifyIds = [];

            data.forEach(element => {
              if(element.orderNumber != "")
                shopifyIds.push(element.orderNumber);
            });


            axios
              .post("/shopify/ordersById", { shopifyIds })
              .then((response) => {
                return response.data;
              })
              .then((res) => {

                //console.log(res);
                
                res.forEach(element => {

                  if(element.cancel_reason != null)
                  {

                    const shareasaleData = data.find((item) => item.orderNumber === element.id.toString());
                    element.shareasale = shareasaleData || null; // Si no lo encuentra, asigna `null`

                    this.orders.push(element);
                    //console.log(element);
                  }
                    
                  
                });
                //console.log(res);

              });

            

            
          });*/
      }
    },
    watch: {
      // Se recalcula el total cada vez que las órdenes cambian
      orders() {
          this.calculateTotalSpend();
      }
  },

    
  }
  </script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// ══════════════════════════════════════════════════════════════
// AWIN / SHAREASALE — same tokens as /pedido-taller-vero/fabricando
// ══════════════════════════════════════════════════════════════

$sa-bg:        #F8FAFC;
$sa-surface:   #FFFFFF;
$sa-surface-2: #F1F5F9;
$sa-border:    #E2E8F0;
$sa-divider:   #EEF2F7;
$sa-ink:       #1E293B;
$sa-text:      #1E293B;
$sa-mute:      #64748B;
$sa-soft:      #94A3B8;
$sa-primary:   #3B82F6;
$sa-cta:       #F97316;
$sa-emerald:   #10B981;
$sa-coral:     #EF4444;
$sa-amber:     #F59E0B;

.sa {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  background: $sa-bg;
  color: $sa-text;
  margin: -2rem;
  padding: 28px 32px 56px;
  min-height: calc(100vh - 4rem);
  font-feature-settings: "tnum", "zero";

  *, *::before, *::after { box-sizing: border-box; }

  .sa-mono {
    font-family: 'JetBrains Mono', monospace;
    font-feature-settings: "tnum", "zero";
  }

  // ──────────── HEADER CARD ────────────
  .sa-dash-header {
    background: $sa-surface;
    border: 1px solid $sa-border;
    border-radius: 14px;
    padding: 18px 20px 16px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .sa-dash-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }

  .sa-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $sa-primary;
    letter-spacing: 0.14em;
    margin-bottom: 4px;

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      background: $sa-cta;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
      animation: sa-pulse 2s infinite;
    }
  }

  .sa-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: $sa-ink;
    line-height: 1.1;
    letter-spacing: -0.01em;
    margin: 0;
  }

  .sa-header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  // ──────────── STAT PILLS ────────────
  .sa-stat-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    background: $sa-bg;
    border: 1px solid $sa-border;
    border-radius: 8px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: $sa-mute;

    i { color: $sa-primary; font-size: 12px; }
    strong {
      font-family: 'JetBrains Mono', monospace;
      font-size: 13px;
      font-weight: 700;
      color: $sa-ink;
      font-feature-settings: "tnum";
    }

    &--total { i { color: $sa-cta; } }

    &--danger {
      background: rgba(239, 68, 68, 0.06);
      border-color: rgba(239, 68, 68, 0.2);
      color: $sa-coral;
      i { color: $sa-coral; }
      strong { color: $sa-coral; }
    }

    &--warning {
      background: rgba(245, 158, 11, 0.06);
      border-color: rgba(245, 158, 11, 0.25);
      color: darken($sa-amber, 8%);
      i { color: $sa-amber; }
      strong { color: darken($sa-amber, 8%); }
    }
  }

  // ──────────── CARD ────────────
  .sa-card {
    background: $sa-surface;
    border: 1px solid $sa-border;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    margin-bottom: 18px;
    overflow: hidden;
  }

  .sa-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px 14px;
    border-bottom: 1px solid $sa-divider;
    flex-wrap: wrap;
  }

  .sa-card-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: $sa-ink;
    letter-spacing: -0.015em;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1.2;
  }

  .sa-chevron {
    font-family: 'JetBrains Mono', monospace;
    font-size: 18px;
    font-weight: 800;
    color: $sa-primary;
    line-height: 1;

    &--danger { color: $sa-coral; }
    &--warning { color: $sa-amber; }
  }

  .sa-card-meta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .sa-card-body {
    padding: 20px;
  }

  .sa-card-desc {
    font-size: 13px;
    color: $sa-mute;
    margin: 0 0 14px;
    line-height: 1.5;

    strong { color: $sa-ink; font-weight: 700; }
  }

  // ──────────── UPLOAD ────────────
  .sa-upload-row {
    display: flex;
    gap: 10px;
    align-items: stretch;
  }

  .sa-upload-input {
    flex: 1;
    min-width: 0;

    .custom-file {
      height: 42px !important;

      .custom-file-label {
        background: $sa-bg !important;
        border: 1px dashed $sa-border !important;
        border-radius: 10px !important;
        color: $sa-mute !important;
        height: 42px !important;
        padding: 11px 16px !important;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: 13px;
        transition: all 150ms ease;

        &::after {
          background: $sa-surface !important;
          border-left: 1px dashed $sa-border !important;
          color: $sa-text !important;
          font-weight: 600;
          border-radius: 0 10px 10px 0 !important;
          padding: 11px 16px !important;
          height: 40px !important;
        }

        &:hover {
          border-color: $sa-primary !important;
          background: rgba(59, 130, 246, 0.03) !important;
        }
      }
    }
  }

  // ──────────── BUTTONS ────────────
  .sa-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 42px;
    padding: 0 18px;
    background: $sa-surface;
    border: 1px solid $sa-border;
    border-radius: 10px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: $sa-text;
    cursor: pointer;
    transition: all 200ms ease;
    white-space: nowrap;

    i { font-size: 12px; }

    &--primary {
      background: $sa-primary;
      border-color: $sa-primary;
      color: #fff;
      &:hover {
        background: darken($sa-primary, 6%);
        border-color: darken($sa-primary, 6%);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
      }
    }

    &--ghost-danger {
      background: $sa-surface;
      color: $sa-coral;
      border-color: $sa-border;
      width: 100%;
      height: 36px;
      font-size: 12px;
      &:hover {
        background: rgba(239, 68, 68, 0.06);
        border-color: $sa-coral;
      }
    }

    &--ghost-warning {
      background: $sa-surface;
      color: darken($sa-amber, 8%);
      border-color: $sa-border;
      width: 100%;
      height: 36px;
      font-size: 12px;
      &:hover {
        background: rgba(245, 158, 11, 0.06);
        border-color: $sa-amber;
      }
    }
  }

  // ──────────── LOADING ────────────
  .sa-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px;
    background: $sa-surface;
    border: 1px solid $sa-border;
    border-radius: 14px;
    color: $sa-mute;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 18px;
  }

  // ──────────── EMPTY STATE ────────────
  .sa-empty {
    background: $sa-surface;
    border: 1px solid $sa-border;
    border-radius: 14px;
    padding: 56px 24px;
    text-align: center;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    .sa-empty-icon {
      width: 64px;
      height: 64px;
      margin: 0 auto 16px;
      background: rgba(16, 185, 129, 0.1);
      color: $sa-emerald;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
    }

    h3 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 18px;
      font-weight: 700;
      color: $sa-ink;
      letter-spacing: -0.01em;
      margin: 0 0 6px;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: $sa-mute;
    }
  }

  // ──────────── ORDER GRID ────────────
  .sa-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
  }

  .sa-order {
    background: $sa-surface;
    border: 1px solid $sa-border;
    border-radius: 12px;
    padding: 14px 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: all 200ms ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 3px;
      height: 100%;
      background: $sa-border;
    }

    &:hover {
      border-color: $sa-soft;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      transform: translateY(-1px);
    }

    &--danger::before { background: $sa-coral; }
    &--warning::before { background: $sa-amber; }
  }

  // Order head
  .sa-order-head {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sa-order-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 700;
    color: $sa-coral;
    letter-spacing: 0.12em;
    text-transform: uppercase;

    .sa-order-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: $sa-coral;
      animation: sa-pulse 2s infinite;
    }

    &--warning {
      color: darken($sa-amber, 8%);
      .sa-order-dot { background: $sa-amber; }
    }
  }

  .sa-order-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: $sa-ink;
    letter-spacing: -0.01em;
  }

  // Order meta rows
  .sa-order-meta {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px 0;
    border-top: 1px solid $sa-divider;
    border-bottom: 1px solid $sa-divider;
  }

  .sa-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    font-size: 11px;
  }

  .sa-meta-k {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 600;
    color: $sa-soft;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  .sa-meta-v {
    font-size: 11.5px;
    font-weight: 500;
    color: $sa-text;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;

    &.sa-mono {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      font-feature-settings: "tnum";
    }
  }

  // Amounts row
  .sa-order-amounts {
    display: flex;
    gap: 10px;
    padding: 4px 0;
  }

  .sa-amount {
    flex: 1;
    min-width: 0;

    &--big { flex: 1.4; }
  }

  .sa-amount-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 600;
    color: $sa-soft;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .sa-amount-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    font-weight: 700;
    color: $sa-ink;
    font-feature-settings: "tnum";

    &--danger { color: $sa-coral; font-size: 16px; }
    &--warning { color: darken($sa-amber, 8%); font-size: 16px; }
  }

  @keyframes sa-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.55; transform: scale(0.85); }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
}

// ══════════ DARK MODE ══════════
.dark-layout .sa {
  background: #161616;
  color: #d0d2d6;

  .sa-dash-header,
  .sa-card,
  .sa-loading,
  .sa-empty,
  .sa-order { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }

  .sa-title { color: #e4e6eb; }
  .sa-card-title { color: #e4e6eb; }
  .sa-card-desc { color: #8e9099; strong { color: #e4e6eb; } }
  .sa-card-head { border-bottom-color: #2c2c2c; }

  .sa-stat-pill { background: #161616; border-color: #2c2c2c; }
  .sa-stat-pill strong { color: #e4e6eb; }

  .sa-upload-input .custom-file-label {
    background: #161616 !important;
    border-color: #2c2c2c !important;
    color: #8e9099 !important;
    &::after { background: #1f1f1f !important; border-left-color: #2c2c2c !important; color: #d0d2d6 !important; }
  }

  .sa-order-meta { border-top-color: #2c2c2c; border-bottom-color: #2c2c2c; }
  .sa-order-name, .sa-amount-value { color: #e4e6eb; }
  .sa-meta-v { color: #d0d2d6; }

  .sa-empty h3 { color: #e4e6eb; }
}
</style>
